import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type FocusEvent,
    type KeyboardEvent,
} from "react";
// eslint-disable-next-line no-restricted-imports
import { Autocomplete as AutocompleteBase } from "@base-ui/react/autocomplete";
import { useLatestRef } from "@design-system/hooks/useLatestRef";
import {
    EMPTY_PICKER_CLASS_NAMES,
    PickerRootListContent,
    PickerRootShell,
} from "./PickerRootShell";
import { PickerInputTriggerPreset } from "./PickerRootTriggers";
import { collectItems, levelItems } from "./PickerTree";
import {
    usePickerListRoot,
    type PickerListRootState,
} from "./usePickerListRoot";
import type {
    PickerCustomValueSelectProps,
    PickerItemValue,
} from "./Picker.types";

/**
 * `Picker.Select` under `allowCustomValue`: a text trigger on Base UI
 * `Autocomplete` whose typed text is the value. The text is a draft:
 * `onValueChange` commits it on Enter (a highlighted suggestion wins) and on
 * blur when it differs from the committed value, while `onInputValueChange`
 * reports every keystroke.
 */
export const PickerSelectCustomValue = ({
    value,
    defaultValue = "",
    onValueChange,
    onInputValueChange,
    items,
    sections,
    nested,
    defaultExpandAll,
    defaultExpandedValues,
    container,
    flyoutDefaults,
    flyoutQueryScope,
    selectableParents,
    flyoutAutoOpen,
    filter,
    filterable = true,
    renderFooter,
    emptyLabel,
    emptyBehavior,
    placeholder,
    openOnInputClick = true,
    inline = false,
    cellEditor,
    autoHighlight = cellEditor !== undefined,
    trigger,
    chevron,
    clearable = false,
    size,
    status = "idle",
    loadingLabel,
    errorLabel,
    hasMore,
    onLoadMore,
    loadMoreStatus,
    open,
    defaultOpen = false,
    onOpenChange,
    disabled = false,
    readOnly = false,
    required = false,
    name,
    id,
    modal = false,
    onBlur,
    "aria-invalid": isInvalid = false,
    "data-forced-state": forcedState,
    ref,
    className,
    classNames = EMPTY_PICKER_CLASS_NAMES,
    children,
}: PickerCustomValueSelectProps) => {
    const [internalValue, setInternalValue] = useState(defaultValue);
    const committedValue = value ?? internalValue;
    // Mirrors committedValue so back-to-back commits in one tick see the latest.
    const committedValueRef = useLatestRef(committedValue);
    const highlightedValueRef = useRef<string | undefined>(undefined);

    const commit = useCallback(
        (text: string) => {
            if (text === committedValueRef.current) {
                return;
            }
            committedValueRef.current = text;
            if (value === undefined) {
                setInternalValue(text);
            }
            onValueChange?.(text);
        },
        [value, onValueChange, committedValueRef],
    );

    // Focus moving into the popup is a suggestion press: that press commits
    // the suggestion, so the draft must not be committed underneath it.
    const commitDraftOnBlur = useCallback(
        (event: FocusEvent<HTMLElement>) => {
            const { relatedTarget, currentTarget } = event;
            if (
                relatedTarget instanceof Element &&
                relatedTarget.closest("[data-picker-popup]") !== null
            ) {
                return;
            }
            if (currentTarget instanceof HTMLInputElement) {
                commit(currentTarget.value);
            }
        },
        [commit],
    );

    // A leaf picked in a flyout level lands as text: its label becomes the
    // draft and is committed at once, like a suggestion press. The root state
    // it needs is created below, so it is reached through a post-commit ref.
    const rootRef = useRef<PickerListRootState<string> | null>(null);
    const commitLeaf = useCallback(
        (values: readonly PickerItemValue[]) => {
            const leaf = values[0];
            const currentRoot = rootRef.current;
            if (leaf === undefined || currentRoot === null) {
                return;
            }
            const text = currentRoot.selection.labelFor(String(leaf));
            currentRoot.resetQuery(text);
            commit(text);
        },
        [commit],
    );

    // The committed text has no value of its own; the leaf whose label it
    // equals reads as selected so it carries the check in every level.
    const matchedValue = useMemo(() => {
        const allItems = items ?? sections?.flatMap((section) => section.items);
        return (
            collectItems(allItems ?? []).find(
                (item) =>
                    levelItems(item).length === 0 &&
                    item.label === committedValue,
            )?.value ?? null
        );
    }, [items, sections, committedValue]);

    const root = usePickerListRoot<string>({
        kind: "combobox",
        isMultiple: false,
        isTextValue: true,
        value: matchedValue,
        defaultValue: null,
        items,
        sections,
        nested,
        defaultExpandAll,
        defaultExpandedValues,
        container,
        flyoutDefaults,
        flyoutQueryScope,
        selectableParents,
        flyoutAutoOpen,
        onLeafCommit: commitLeaf,
        filter,
        shouldFilter: filterable,
        open,
        defaultOpen,
        isAlwaysOpen: inline,
        isAutoHighlight: autoHighlight,
        cellEditor,
        onOpenChange,
        defaultInputValue: committedValue,
        onInputValueChange,
        emptyBehavior,
        status,
        hasMore,
        onLoadMore,
        loadMoreStatus,
        renderFooter,
        disabled,
        isInvalid,
        classNames,
        forcedState,
        // Neither free-text field shows a chevron unless asked.
        chevron: chevron ?? "none",
        explicitChevron: chevron,
        onBeforeTriggerBlur: commitDraftOnBlur,
        onBlur,
    });
    const { resetQuery } = root;
    useLayoutEffect(() => {
        rootRef.current = root;
    });

    useEffect(() => {
        if (value !== undefined) {
            resetQuery(value);
        }
    }, [value, resetQuery]);

    // A highlighted suggestion is committed by Base UI's item press. Raw text
    // is committed here and the Enter is consumed; an unchanged draft lets an
    // enclosing form submit.
    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (
            event.key !== "Enter" ||
            highlightedValueRef.current !== undefined
        ) {
            return;
        }
        const draft = event.currentTarget.value;
        if (draft === committedValueRef.current) {
            return;
        }
        event.preventDefault();
        commit(draft);
    };

    return (
        <PickerRootShell root={root}>
            <AutocompleteBase.Root
                value={root.query}
                onValueChange={(text, details) => {
                    root.handleInputValueChange(text, details.reason);
                    if (details.reason === "item-press") {
                        commit(text);
                    }
                }}
                onItemHighlighted={(highlightedValue) => {
                    highlightedValueRef.current = highlightedValue;
                }}
                itemToStringValue={root.selection.labelFor}
                open={root.isOpen}
                onOpenChange={root.handleBaseOpenChange}
                openOnInputClick={openOnInputClick}
                inline={inline}
                autoHighlight={autoHighlight}
                disabled={disabled}
                readOnly={readOnly}
                required={required}
                name={name}
                id={id}
                modal={modal}
            >
                <PickerRootListContent
                    root={root}
                    trigger={
                        <PickerInputTriggerPreset
                            trigger={trigger}
                            placeholder={placeholder}
                            readOnly={readOnly}
                            size={size}
                            clearable={clearable}
                            className={className}
                            ref={ref}
                            onKeyDown={handleKeyDown}
                        />
                    }
                    hasPopupSearch={false}
                    isInline={inline}
                    status={status}
                    loadingLabel={loadingLabel}
                    errorLabel={errorLabel}
                    emptyLabel={emptyLabel}
                    emptyBehavior={emptyBehavior}
                >
                    {children}
                </PickerRootListContent>
            </AutocompleteBase.Root>
        </PickerRootShell>
    );
};
